import classNames from "classnames";
import React, { useEffect, useMemo, useRef } from "react";
import { Svg } from "../../../../Shared/Assets/Svg/React";
import {
  Button,
  ButtonColor,
  ButtonSize,
  ButtonTarget
} from "../../../../Shared/Buttons/React";
import { useProvetStatic, useTranslationContext } from "../../context";
import { ProvetDepartment } from "../../types";
import { getOldBookingUrl } from "../../utilities/departmentUtilities";
import { SeparateBookingNote } from "./oldBookingListItem";

export interface DepartmentListItemProps {
  department: ProvetDepartment;
  onSelectDepartment: (department: ProvetDepartment) => void;
  oldBookingDomain?: string;
  vertical?: boolean;
}

export const DepartmentListItem = ({
  department,
  onSelectDepartment,
  oldBookingDomain,
  vertical = false
}: DepartmentListItemProps) => {
  const translationContext = useTranslationContext();
  const { language } = useProvetStatic();
  // Undefined (no button) when the old booking domain isn't configured
  const oldBookingUrl = department.isActive
    ? undefined
    : getOldBookingUrl(oldBookingDomain, department.id, language);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Use native DOM event listener to ensure it works even after Google Maps moves the element
  useEffect(() => {
    const button = buttonRef.current;
    if (!button) {
      return;
    }

    const handleClick = () => {
      onSelectDepartment(department);

      // Close the popover after selection
      const popoverId = `department-popover-${department.id}`;
      const popoverElement = document.getElementById(popoverId);

      if (popoverElement && window.ani_getPopover) {
        const popoverRecord = window.ani_getPopover(popoverElement);
        if (popoverRecord && typeof popoverRecord.close === "function") {
          popoverRecord.close();
        }
      }
    };

    button.addEventListener("click", handleClick);

    return () => {
      button.removeEventListener("click", handleClick);
    };
  }, [onSelectDepartment, department]);

  const getFormattedAddress = (department: ProvetDepartment) => {
    const addressSegments: string[] = [];

    if (!!department.address_info?.street_address_1) {
      addressSegments.push(department.address_info.street_address_1);
    }

    if (!!department.address_info?.city) {
      addressSegments.push(
        `${department.address_info?.zip_code} ${department.address_info?.city} `
      );
    }

    return addressSegments.length > 0 ? addressSegments.join(",") : "";
  };

  const containerClasses = useMemo(
    () =>
      classNames("flex gap-sm justify-between", {
        "flex-col": vertical,
        "flex-row": !vertical
      }),
    [vertical]
  );

  const titleClasses = useMemo(
    () =>
      classNames("text-t-md lg:text-t-lg font-semibold", {
        "w-[calc(100%-30px)]": vertical
      }),
    [vertical]
  );

  return (
    <div className={containerClasses}>
      <div className="flex flex-col gap-md">
        {department.isActive ? (
          <div className={titleClasses}>{department.name}</div>
        ) : (
          // Name and note share a line, like the list row; the note wraps below when needed
          <div className="flex flex-wrap items-center gap-x-xs gap-y-xxs">
            <div className={titleClasses}>{department.name}</div>
            <SeparateBookingNote />
          </div>
        )}
        <div className="flex gap-lg flex-col sm:gap-xs md:gap-md">
          {!!department.address_info?.phone && (
            <a
              className="flex items-center gap-xs text-t-sm text-t-neutral-700"
              href={`tel:${department.address_info.phone}`}
            >
              <Svg href="/svg/system/phone.svg" />

              <span>{department.address_info.phone}</span>
            </a>
          )}

          {department.address_info?.street_address_1 &&
            !!department.address_info?.city && (
              <p className="flex items-center gap-xs text-t-sm text-t-neutral-700">
                <Svg href="/svg/system/pin.svg" />
                <span>{getFormattedAddress(department)}</span>
              </p>
            )}
        </div>
      </div>
      <div>
        {department.isActive && (
          <Button
            ref={buttonRef}
            size={ButtonSize.MD}
            color={ButtonColor.OutlineDarkBlue}
            data-online-booking-action="confirmClinicFromMapMarker"
          >
            {translationContext?.translations?.select}
          </Button>
        )}
        {!department.isActive && oldBookingUrl && (
          <Button
            size={ButtonSize.MD}
            href={oldBookingUrl}
            color={ButtonColor.OutlineDarkBlue}
            target={ButtonTarget.Blank}
            newWindowScreenReaderText={translationContext?.translations?.newWindow}
            title={translationContext?.translations?.goToBookingPage}
            data-online-booking-action="goToOldBooking"
          >
            {translationContext?.translations?.goToBookingPage}
          </Button>
        )}
      </div>
    </div>
  );
};
